import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Product, Batch, BatchDetail, Material, SubGroup, PackagingOrder } from './mockData';
import { FilterState } from './FilterBar';
import { ChevronRight, ChevronLeft, Settings, UtensilsCrossed, Cloud, Scissors, Package, Info, X } from 'lucide-react';
interface ChargeDetailProps {
  product: Product;
  batch: Batch;
  details: BatchDetail | null;
  filters: FilterState;
  visibleColumns: string[];
  onBackToProduct: () => void;
  onBackToBatchList: () => void;
  returnToTransfer?: boolean;
  onBackToTransfer?: () => void;
}
const iconMap = {
  Settings: Settings,
  UtensilsCrossed: UtensilsCrossed,
  Cloud: Cloud,
  Scissors: Scissors,
  Package: Package
};
const ALL_COLUMNS: {
  key: string;
  label: string;
  align?: 'right';
}[] = [{
  key: 'name',
  label: 'Material'
}, {
  key: 'batchNo',
  label: 'Chargen-Nr.'
}, {
  key: 'supplier',
  label: 'Lieferant'
}, {
  key: 'scan',
  label: 'Scan'
}, {
  key: 'mhd',
  label: 'MHD'
}, {
  key: 'we',
  label: 'WE'
}, {
  key: 'quantity',
  label: 'Menge',
  align: 'right'
}];
// Per-section default hidden columns. These are removed from the user's visible-columns
// set on a per-section basis (Spalten-Toggle bleibt global, dient als Obergrenze).
const SECTION_HIDDEN_COLS: Record<string, string[]> = {
  Füllerei: ['we', 'quantity'],
  Räucherei: ['mhd', 'we', 'quantity']
};
// Shared column widths so columns line up vertically across every section table.
// Pixel-based; tables use table-layout: fixed and only render the columns they need —
// but each rendered column always claims the same width as elsewhere on the page.
const COL_PX_WIDTH: Record<string, number> = {
  name: 280,
  batchNo: 200,
  supplier: 200,
  scan: 240,
  mhd: 140,
  we: 130,
  quantity: 120,
  // Verpackung-specific
  action: 130
};
function colsForSection<T extends {
  key: string;
}>(sectionTitle: string, cols: T[]): T[] {
  const hidden = SECTION_HIDDEN_COLS[sectionTitle];
  if (!hidden || hidden.length === 0) return cols;
  return cols.filter(c => !hidden.includes(c.key));
}
function matchesSearch(m: Material, filters: FilterState): boolean {
  const term = filters.search.trim().toLowerCase();
  if (!term) return true;
  return filters.searchFields.some(fieldKey => {
    if (fieldKey === 'Material') return m.name.toLowerCase().includes(term);
    if (fieldKey === 'Lieferant') return m.supplier.toLowerCase().includes(term);
    if (fieldKey === 'Chargen-Nr.') return m.batchNo.toLowerCase().includes(term);
    if (fieldKey === 'MHD') return m.mhd.toLowerCase().includes(term);
    return false;
  });
}
export function ChargeDetail({
  product,
  batch,
  details,
  filters,
  visibleColumns,
  onBackToProduct,
  onBackToBatchList,
  returnToTransfer,
  onBackToTransfer
}: ChargeDetailProps) {
  if (!details) {
    return <div className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 py-12 text-center text-gray-500" data-id="element-8">
        Keine Detaildaten für diese Charge verfügbar.
      </div>;
  }
  const cols = ALL_COLUMNS.filter(c => visibleColumns.includes(c.key));
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 pb-12" data-id="element-9">
      {/* Breadcrumb */}
      <nav className="flex items-center text-sm text-gray-500 mb-6 flex-wrap gap-y-2" data-id="element-10">
        {returnToTransfer ? <>
            <button onClick={onBackToTransfer} className="hover:text-[#E87722] flex items-center transition-colors" data-id="element-11">
              <ChevronLeft className="w-4 h-4 mr-1" data-id="element-12" />
              Chargen übertragen
            </button>
            <ChevronRight className="w-4 h-4 mx-2 text-gray-400" data-id="element-13" />
            <span className="font-medium text-gray-900" data-id="element-14">
              {batch.batchNumber}
            </span>
          </> : <>
            <button onClick={onBackToProduct} className="hover:text-[#E87722] flex items-center transition-colors" data-id="element-15">
              <ChevronLeft className="w-4 h-4 mr-1" data-id="element-16" />
              Produkte
            </button>
            <ChevronRight className="w-4 h-4 mx-2 text-gray-400" data-id="element-17" />
            <button onClick={onBackToBatchList} className="hover:text-[#E87722] transition-colors" data-id="element-18">
              {product.name}
            </button>
            <ChevronRight className="w-4 h-4 mx-2 text-gray-400" data-id="element-19" />
            <span className="font-medium text-gray-900" data-id="element-20">
              {batch.batchNumber}
            </span>
          </>}
      </nav>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-8" data-id="element-21">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-22">
          <div data-id="element-23">
            <h1 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-24">
              Charge {batch.batchNumber}
            </h1>
            <p className="text-gray-500" data-id="element-25">
              {product.name} • Produziert am {batch.productionDate}
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-26">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${batch.status === 'Abgeschlossen' ? 'bg-green-100 text-green-800' : batch.status === 'Bereit zum Verpacken' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-27">
              {batch.status}
            </span>
            <button className="px-4 py-2 bg-[#E87722] text-white rounded-lg text-sm font-medium hover:bg-[#d0681b] transition-colors shadow-sm" data-id="element-28">
              Bericht exportieren
            </button>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-6" data-id="element-29">
        {details.sections.map((section, index) => {
        const Icon = iconMap[section.iconName];
        return <motion.div key={section.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.05
        }} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-30">
              {/* Section Header */}
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50 flex items-center gap-3" data-id="element-31">
                <div className="p-2 bg-[#16A34A]/10 rounded-full" data-id="element-32">
                  <Icon className="w-5 h-5 text-[#16A34A]" data-id="element-33" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-34">
                  {section.title}
                </h2>
                {section.subGroups && section.subGroups.length > 0 && <span className="ml-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full font-medium" data-id="element-35">
                    {section.subGroups.length}{' '}
                    {section.title === 'Räucherei' ? 'Kammern' : 'Paletten'}
                  </span>}
                {section.packagingOrders && section.packagingOrders.length > 0 && <span className="ml-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full font-medium" data-id="element-36">
                      {section.packagingOrders.length} Aufträge
                    </span>}
              </div>

              {/* Body: packagingOrders | Schälerei palette rows | subGroups tabs | flat */}
              {section.packagingOrders && section.packagingOrders.length > 0 ? <PackagingTable orders={section.packagingOrders} data-id="element-37" /> : section.title === 'Schälerei' && section.subGroups && section.subGroups.length > 0 ? <PaletteTable palettes={section.subGroups} data-id="element-38" /> : section.subGroups && section.subGroups.length > 0 ? <SubGroupTabs subGroups={section.subGroups} cols={colsForSection(section.title, cols)} filters={filters} sectionTitle={section.title} data-id="element-39" /> : <MaterialsTable materials={section.materials.filter(m => matchesSearch(m, filters))} cols={colsForSection(section.title, cols)} data-id="element-40" />}
            </motion.div>;
      })}
      </div>
    </motion.div>;
}
// ---------- SubGroupTabs ----------
function SubGroupTabs({
  subGroups,
  cols,
  filters,
  sectionTitle
}: {
  subGroups: SubGroup[];
  cols: {
    key: string;
    label: string;
    align?: 'right';
  }[];
  filters: FilterState;
  sectionTitle: string;
}) {
  const [activeId, setActiveId] = useState(subGroups[0]?.id);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  // Reset active when subGroups change
  useEffect(() => {
    if (!subGroups.find(g => g.id === activeId)) {
      setActiveId(subGroups[0]?.id);
    }
  }, [subGroups, activeId]);
  // Track scroll state for the chevron buttons
  const updateScrollState = () => {
    const el = tabsRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };
  useEffect(() => {
    updateScrollState();
    const el = tabsRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [subGroups.length]);
  const scrollBy = (dir: 1 | -1) => {
    const el = tabsRef.current;
    if (!el) return;
    el.scrollBy({
      left: dir * 200,
      behavior: 'smooth'
    });
  };
  const active = subGroups.find(g => g.id === activeId) ?? subGroups[0];
  const filteredMaterials = active.materials.filter(m => matchesSearch(m, filters));
  // Count matches per tab to surface filter hits
  const matchCounts = useMemo(() => Object.fromEntries(subGroups.map(g => [g.id, g.materials.filter(m => matchesSearch(m, filters)).length])), [subGroups, filters]);
  const term = filters.search.trim();
  return <div data-id="element-41">
      {/* Tab bar with scroll affordance */}
      <div className="relative border-b border-gray-200 bg-white" data-id="element-42">
        {canScrollLeft && <button onClick={() => scrollBy(-1)} className="absolute left-0 top-0 bottom-0 z-10 px-1.5 bg-gradient-to-r from-white via-white to-transparent flex items-center" aria-label="Nach links scrollen" data-id="element-43">
            <ChevronLeft className="w-4 h-4 text-gray-500" data-id="element-44" />
          </button>}
        {canScrollRight && <button onClick={() => scrollBy(1)} className="absolute right-0 top-0 bottom-0 z-10 px-1.5 bg-gradient-to-l from-white via-white to-transparent flex items-center" aria-label="Nach rechts scrollen" data-id="element-45">
            <ChevronRight className="w-4 h-4 text-gray-500" data-id="element-46" />
          </button>}

        <div ref={tabsRef} className="flex overflow-x-auto scrollbar-thin scroll-smooth px-2" style={{
        scrollbarWidth: 'thin'
      }} data-id="element-47">
          {subGroups.map(g => {
          const isActive = g.id === activeId;
          const count = matchCounts[g.id] ?? 0;
          const filteredOut = term && count === 0;
          return <button key={g.id} onClick={() => setActiveId(g.id)} className={`relative whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${isActive ? 'text-[#E87722]' : filteredOut ? 'text-gray-300 hover:text-gray-500' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-48">
                {g.label}
                <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold ${isActive ? 'bg-[#E87722] text-white' : term && count > 0 ? 'bg-[#E87722]/15 text-[#E87722]' : 'bg-gray-100 text-gray-500'}`} data-id="element-49">
                  {term ? count : g.materials.length}
                </span>
                {isActive && <motion.div layoutId={`tab-underline-${sectionTitle}`} className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#E87722] rounded-t" data-id="element-50" />}
              </button>;
        })}
        </div>
      </div>

      {/* Active sub-group table */}
      <AnimatePresence mode="wait" data-id="element-51">
        <motion.div key={active.id} initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.15
      }} data-id="element-52">
          <MaterialsTable materials={filteredMaterials} cols={cols} data-id="element-53" />
        </motion.div>
      </AnimatePresence>
    </div>;
}
// ---------- PackagingTable ----------
function PackagingTable({
  orders
}: {
  orders: PackagingOrder[];
}) {
  const [selectedOrder, setSelectedOrder] = useState<PackagingOrder | null>(null);
  return <>
      <div className="overflow-x-auto" data-id="element-54">
        <table className="w-full divide-y divide-gray-200 table-fixed" data-id="element-55">
          <colgroup data-id="element-56">
            <col style={{
            width: COL_PX_WIDTH.name
          }} data-id="element-57" />
            <col style={{
            width: COL_PX_WIDTH.batchNo
          }} data-id="element-58" />
            <col style={{
            width: COL_PX_WIDTH.supplier
          }} data-id="element-59" />
            <col style={{
            width: COL_PX_WIDTH.scan
          }} data-id="element-60" />
            <col style={{
            width: COL_PX_WIDTH.action
          }} data-id="element-61" />
            <col data-id="element-62" />
          </colgroup>
          <thead className="bg-white" data-id="element-63">
            <tr data-id="element-64">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-65">
                Kunde
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-66">
                Bestellnummer
              </th>
              <th data-id="element-67"></th>
              <th data-id="element-68"></th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-69">
                <span className="sr-only" data-id="element-70">Details</span>
              </th>
              <th data-id="element-71"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100" data-id="element-72">
            {orders.map(order => <tr key={order.id} className="hover:bg-gray-50 transition-colors" data-id="element-73">
                <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-74">
                  {order.customer}
                </td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600 font-mono" data-id="element-75">
                  {order.orderNumber}
                </td>
                <td data-id="element-76"></td>
                <td data-id="element-77"></td>
                <td className="px-6 py-3 whitespace-nowrap text-right" data-id="element-78">
                  <button onClick={() => setSelectedOrder(order)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-[#E87722] hover:bg-[#E87722]/10 transition-colors" data-id="element-79">
                    <Info className="w-3.5 h-3.5" data-id="element-80" />
                    Details
                  </button>
                </td>
                <td data-id="element-81"></td>
              </tr>)}
          </tbody>
        </table>
      </div>

      <AnimatePresence data-id="element-82">
        {selectedOrder && <PackagingDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} data-id="element-83" />}
      </AnimatePresence>
    </>;
}
type OrderTab = 'chargen' | 'rohstoffe' | 'produkte';
function PackagingDetailModal({
  order,
  onClose
}: {
  order: PackagingOrder;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<OrderTab>('chargen');
  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const tabs: {
    id: OrderTab;
    label: string;
    linkLabel: string;
    items: typeof order.chargen;
  }[] = [{
    id: 'chargen',
    label: 'CHARGEN',
    linkLabel: 'Zur Charge',
    items: order.chargen ?? []
  }, {
    id: 'rohstoffe',
    label: 'ROHSTOFFE',
    linkLabel: 'Zum Rohstoff',
    items: order.rohstoffe ?? []
  }, {
    id: 'produkte',
    label: 'PRODUKTE',
    linkLabel: 'Zum Produkt',
    items: order.produkte ?? []
  }];
  const currentTab = tabs.find(t => t.id === tab) ?? tabs[0];
  const numberColLabel = tab === 'chargen' ? 'Chargen-Nr.' : tab === 'rohstoffe' ? 'Rohstoff-Nr.' : 'Produkt-Nr.';
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.15
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose} data-id="element-84">
      <motion.div initial={{
      scale: 0.96,
      opacity: 0,
      y: 12
    }} animate={{
      scale: 1,
      opacity: 1,
      y: 0
    }} exit={{
      scale: 0.96,
      opacity: 0,
      y: 12
    }} transition={{
      duration: 0.18
    }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden" data-id="element-85">
        {/* Header */}
        <div className="px-6 py-5 flex items-start justify-between gap-4" data-id="element-86">
          <h3 className="text-2xl font-semibold text-gray-900" data-id="element-87">
            Bestellung: {order.orderNumber}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition-colors -mt-1 -mr-1" aria-label="Schließen" data-id="element-88">
            <X className="w-5 h-5" data-id="element-89" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-gray-200" data-id="element-90">
          <div className="flex gap-6" data-id="element-91">
            {tabs.map(t => {
            const isActive = t.id === tab;
            return <button key={t.id} onClick={() => setTab(t.id)} className={`relative py-3 text-sm font-semibold tracking-wider transition-colors ${isActive ? 'text-[#E87722]' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-92">
                  {t.label}
                  {isActive && <motion.div layoutId="order-tab-underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#E87722] rounded-t" data-id="element-93" />}
                </button>;
          })}
          </div>
        </div>

        {/* Tab content */}
        <div className="p-6" data-id="element-94">
          <div className="rounded-lg border border-gray-200 overflow-hidden" data-id="element-95">
            <div className="grid grid-cols-[1fr_1fr_auto] gap-4 px-5 py-3 bg-gray-50 border-b border-gray-200 text-sm font-semibold text-gray-700" data-id="element-96">
              <div data-id="element-97">{numberColLabel}</div>
              <div data-id="element-98">Scan</div>
              <div className="w-20" data-id="element-99" />
            </div>

            <AnimatePresence mode="wait" data-id="element-100">
              <motion.div key={tab} initial={{
              opacity: 0,
              y: 4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} transition={{
              duration: 0.12
            }} className="divide-y divide-gray-100" data-id="element-101">
                {currentTab.items && currentTab.items.length > 0 ? currentTab.items.map(item => <div key={item.id} className="grid grid-cols-[1fr_1fr_auto] gap-4 px-5 py-3.5 items-center hover:bg-gray-50 transition-colors" data-id="element-102">
                      <div className="text-sm text-gray-900 font-mono" data-id="element-103">
                        {item.number}
                      </div>
                      <div className="text-sm text-gray-600 tabular-nums" data-id="element-104">
                        {item.scan}
                      </div>
                      <button className="text-sm text-[#E87722] hover:text-[#d0681b] underline underline-offset-2 font-medium whitespace-nowrap" data-id="element-105">
                        {currentTab.linkLabel}
                      </button>
                    </div>) : <div className="px-5 py-8 text-center text-sm text-gray-400" data-id="element-106">
                    Keine Einträge.
                  </div>}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
// ---------- PaletteTable (Schälerei: flat palette rows) ----------
function PaletteTable({
  palettes
}: {
  palettes: SubGroup[];
}) {
  return <div className="overflow-x-auto" data-id="element-107">
      <table className="w-full divide-y divide-gray-200 table-fixed" data-id="element-108">
        <colgroup data-id="element-109">
          <col style={{
          width: COL_PX_WIDTH.name
        }} data-id="element-110" />
          <col style={{
          width: COL_PX_WIDTH.batchNo
        }} data-id="element-111" />
          <col style={{
          width: COL_PX_WIDTH.supplier
        }} data-id="element-112" />
          <col style={{
          width: COL_PX_WIDTH.scan
        }} data-id="element-113" />
          <col data-id="element-114" />
        </colgroup>
        <thead className="bg-white" data-id="element-115">
          <tr data-id="element-116">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-117">
              Paletten-Nr.
            </th>
            <th data-id="element-118"></th>
            <th data-id="element-119"></th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-120">
              Scan
            </th>
            <th data-id="element-121"></th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100" data-id="element-122">
          {palettes.map((palette, i) => <motion.tr key={palette.id} initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.06,
          duration: 0.25,
          ease: 'easeOut'
        }} className="hover:bg-gray-50 transition-colors" data-id="element-123">
              <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900 font-mono" data-id="element-124">
                {palette.label}
              </td>
              <td data-id="element-125"></td>
              <td data-id="element-126"></td>
              <td className="px-6 py-3 whitespace-nowrap text-sm" data-id="element-127">
                {palette.scan && palette.scan !== '-' ? <div className="flex items-center gap-2" data-id="element-128">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-800" data-id="element-129">
                      OK
                    </span>
                    <span className="text-xs text-gray-600 tabular-nums whitespace-nowrap" data-id="element-130">
                      {palette.scan}
                    </span>
                  </div> : <span className="text-gray-400" data-id="element-131">–</span>}
              </td>
              <td data-id="element-132"></td>
            </motion.tr>)}
        </tbody>
      </table>
    </div>;
}
// ---------- MaterialsTable ----------
function MaterialsTable({
  materials,
  cols
}: {
  materials: Material[];
  cols: {
    key: string;
    label: string;
    align?: 'right';
  }[];
}) {
  return <div className="overflow-x-auto" data-id="element-133">
      <table className="w-full divide-y divide-gray-200 table-fixed" data-id="element-134">
        <colgroup data-id="element-135">
          {cols.map(c => <col key={c.key} style={{
          width: COL_PX_WIDTH[c.key] ?? 160
        }} data-id="element-136" />)}
          <col data-id="element-137" />
        </colgroup>
        <thead className="bg-white" data-id="element-138">
          <tr data-id="element-139">
            {cols.map(c => <th key={c.key} className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${c.align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-140">
                {c.label}
              </th>)}
            <th data-id="element-141"></th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100" data-id="element-142">
          {materials.map(mat => <tr key={mat.id} className="hover:bg-gray-50 transition-colors" data-id="element-143">
              {cols.map(c => <td key={c.key} className={`px-6 py-3 whitespace-nowrap text-sm ${c.align === 'right' ? 'text-right font-medium text-gray-900' : ''} ${c.key === 'name' ? 'font-medium text-gray-900' : 'text-gray-600'} ${c.key === 'batchNo' ? 'font-mono' : ''}`} data-id="element-144">
                  {c.key === 'scan' ? mat.scan && mat.scan !== '-' ? <div className="flex items-center gap-2" data-id="element-145">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-800" data-id="element-146">
                          OK
                        </span>
                        <span className="text-xs text-gray-600 tabular-nums whitespace-nowrap" data-id="element-147">
                          {mat.scan}
                        </span>
                      </div> : <span className="text-gray-400" data-id="element-148">{mat.scan}</span> : (mat as any)[c.key]}
                </td>)}
              <td data-id="element-149"></td>
            </tr>)}
          {materials.length === 0 && <tr data-id="element-150">
              <td colSpan={cols.length + 1} className="px-6 py-6 text-center text-sm text-gray-400" data-id="element-151">
                Keine Einträge entsprechen den Filtern.
              </td>
            </tr>}
        </tbody>
      </table>
    </div>;
}